/**
 * observability/formato.js — funções puras da tela Observabilidade de Agentes.
 *
 * Formatação pt-BR (custo SEMPRE em US$), período do filtro, ordenação e a cascata da
 * execução. Os agregados (resumo do período, portão por versão de prompt, loops por
 * projeto, erros por tipo) vêm prontos da API — nada disso é calculado no navegador.
 * Regra: ausente → "—", nunca zero fictício.
 */
import { VAZIO, numeroOuNulo } from '@/lib/format';

export const SEM_PERMISSAO_DE_CUSTO = 'Sem permissão para ver custos';

// ─── Rótulos dos domínios fechados (migration 0101) ──────────────────────────

export const TIPOS_DE_RODADA = [
  'principal', 'reforco', 'reabertura', 'revisao', 'rerevisao', 'blueprint_fix',
  'estilo_fix', 'cobertura', 'verify_reparo', 'continuacao', 'delegacao',
  'exploracao', 'critica', 'refino', 'fallback',
];
export const STATUS = ['ok', 'erro', 'sem_mudanca', 'cancelado', 'orcamento'];
export const CANAIS = ['motor', 'ai_core'];

const ROTULOS = {
  principal: 'Principal', reforco: 'Reforço', reabertura: 'Reabertura', revisao: 'Revisão',
  rerevisao: 'Re-revisão', blueprint_fix: 'Correção de blueprint', estilo_fix: 'Correção de estilo',
  cobertura: 'Cobertura', verify_reparo: 'Reparo do verify', continuacao: 'Continuação',
  delegacao: 'Delegação', exploracao: 'Exploração do repositório', critica: 'Autocrítica',
  refino: 'Refino', fallback: 'Fallback',
  ok: 'OK', erro: 'Erro', sem_mudanca: 'Sem mudança', cancelado: 'Cancelado',
  orcamento: 'Teto de orçamento',
  motor: 'Motor (Claude Code)', ai_core: 'AI Core',
  elenco: 'Elenco', papel_padrao: 'Papel padrão', fixo: 'Fixo', sem_agente: 'Sem agente',
};

/** Rótulo pt-BR de um valor de domínio; valor desconhecido aparece como veio. */
export function rotulo(valor) {
  if (valor === null || valor === undefined || valor === '') return VAZIO;
  return ROTULOS[valor] || String(valor);
}

// ─── Formatação ──────────────────────────────────────────────────────────────

/** US$ com 2 casas; abaixo de US$ 1 usa até 4 e abaixo de 1 centavo até 6 (invocação custa centavos). */
export function custo(v) {
  const n = numeroOuNulo(v);
  if (n == null) return VAZIO;
  const abs = Math.abs(n);
  const max = abs === 0 || abs >= 1 ? 2 : abs < 0.01 ? 6 : 4;
  return `US$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: max })}`;
}

export function inteiro(v) {
  const n = numeroOuNulo(v);
  return n == null ? VAZIO : Math.round(n).toLocaleString('pt-BR');
}

export function pct(v) {
  const n = numeroOuNulo(v);
  if (n == null) return VAZIO;
  return `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

export function decimal(v, casas = 2) {
  const n = numeroOuNulo(v);
  if (n == null) return VAZIO;
  return n.toLocaleString('pt-BR', { maximumFractionDigits: casas });
}

/** Duração legível: 850 ms · 12,3 s · 3 min 20 s · 1 h 05 min. */
export function duracao(ms) {
  const n = numeroOuNulo(ms);
  if (n == null) return VAZIO;
  if (n < 1000) return `${Math.round(n).toLocaleString('pt-BR')} ms`;
  if (n < 60_000) return `${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s`;
  const seg = Math.round(n / 1000);
  if (seg < 3600) return `${Math.floor(seg / 60)} min ${String(seg % 60).padStart(2, '0')} s`;
  const min = Math.round(seg / 60);
  return `${Math.floor(min / 60).toLocaleString('pt-BR')} h ${String(min % 60).padStart(2, '0')} min`;
}

/** "2026-10-05" → "05/10". */
export function diaCurto(chave) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(chave || ''));
  return m ? `${m[3]}/${m[2]}` : String(chave || VAZIO);
}

/** ISO → "05/10/2026" (dia em UTC — a série da API é agrupada por dia UTC). */
export function diaLongo(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : VAZIO;
}

/** UUID abreviado para tabela (o valor inteiro vai no `title`). */
export function idCurto(id) {
  const s = String(id || '');
  return s.length > 8 ? `${s.slice(0, 8)}…` : s || VAZIO;
}

// ─── Período ─────────────────────────────────────────────────────────────────

export const PRESETS = [
  { id: '7', dias: 7, label: 'Últimos 7 dias' },
  { id: '30', dias: 30, label: 'Últimos 30 dias' },
  { id: '90', dias: 90, label: 'Últimos 90 dias' },
  { id: '365', dias: 365, label: 'Últimos 12 meses' },
  { id: 'personalizado', dias: null, label: 'Período personalizado' },
];

const DIA_MS = 86_400_000;
const soDia = (d) => d.toISOString().slice(0, 10);

/**
 * `{ de, ate }` (ISO, UTC) para a API. Preset: do início do dia UTC de N dias atrás até
 * agora (`ate` omitido). Personalizado: do início de `de` ao FIM de `ate` (a API trata
 * `ate` como exclusivo, por isso vai o dia seguinte).
 */
export function periodoDoFiltro({ preset = '30', de = '', ate = '' } = {}, agora = new Date()) {
  const p = PRESETS.find((x) => x.id === preset) || PRESETS[1];
  if (p.dias != null) {
    return { de: `${soDia(new Date(agora.getTime() - p.dias * DIA_MS))}T00:00:00Z`, ate: undefined };
  }
  const valido = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');
  return {
    de: valido(de) ? `${de}T00:00:00Z` : undefined,
    ate: valido(ate)
      ? `${soDia(new Date(Date.parse(`${ate}T00:00:00Z`) + DIA_MS))}T00:00:00Z`
      : undefined,
  };
}

/** Série por dia com os dias sem invocação preenchidos com zero (do 1º ao último dia). */
export function preencherDias(serie = []) {
  const pontos = [...serie].sort((a, b) => String(a.chave).localeCompare(String(b.chave)));
  if (pontos.length < 2) return pontos;
  const porDia = new Map(pontos.map((p) => [p.chave, p]));
  const ini = Date.parse(`${pontos[0].chave}T00:00:00Z`);
  const fim = Date.parse(`${pontos[pontos.length - 1].chave}T00:00:00Z`);
  if (!Number.isFinite(ini) || !Number.isFinite(fim)) return pontos;
  const saida = [];
  for (let t = ini; t <= fim; t += DIA_MS) {
    const chave = soDia(new Date(t));
    saida.push(porDia.get(chave) || { chave, invocacoes: 0, erros: 0, custo_total_usd: 0, vazio: true });
  }
  return saida;
}

// ─── Ordenação ───────────────────────────────────────────────────────────────

const soma = (lista, campo) => lista.reduce((a, x) => a + (Number(x?.[campo]) || 0), 0);

/** Ordena sem mexer no original; valor ausente vai sempre para o fim. */
export function ordenar(lista = [], campo, direcao = 'desc') {
  const sinal = direcao === 'asc' ? 1 : -1;
  return [...lista].sort((a, b) => {
    const va = a?.[campo];
    const vb = b?.[campo];
    const na = va === null || va === undefined;
    const nb = vb === null || vb === undefined;
    if (na && nb) return 0;
    if (na) return 1;
    if (nb) return -1;
    if (typeof va === 'string' || typeof vb === 'string') {
      return sinal * String(va).localeCompare(String(vb), 'pt-BR');
    }
    return sinal * (Number(va) - Number(vb));
  });
}

// ─── Execução: cascata etapa → rodada ────────────────────────────────────────

export const SEM_ETAPA = '(sem etapa)';

/** Soma de custos; se algum vier `null` (sem permissão), o total também é `null`. */
function somaDeCusto(itens, campo) {
  if (itens.some((x) => x?.[campo] === null || x?.[campo] === undefined)) return null;
  return itens.reduce((a, x) => a + Number(x[campo] || 0), 0);
}

function totais(invocacoes) {
  const comDuracao = invocacoes.filter((i) => i.duracao_ms != null);
  return {
    invocacoes: invocacoes.length,
    erros: invocacoes.filter((i) => i.status === 'erro').length,
    custoUsd: invocacoes.length ? somaDeCusto(invocacoes, 'cost_usd') : null,
    inputTokens: soma(invocacoes, 'input_tokens'),
    outputTokens: soma(invocacoes, 'output_tokens'),
    cacheReadTokens: soma(invocacoes, 'cache_read_tokens'),
    cacheCreationTokens: soma(invocacoes, 'cache_creation_tokens'),
    duracaoMs: comDuracao.length ? soma(comDuracao, 'duracao_ms') : null,
  };
}

/**
 * Linha do tempo da API → etapas (na ordem em que apareceram) → rodadas
 * (tipo de rodada + nº) → invocações, com o resultado do portão de cada rodada.
 * Resultado de etapa sem invocação correspondente vira rodada própria — nada some.
 */
export function cascataDaExecucao(invocacoes = [], resultados = []) {
  const etapas = new Map();
  const etapaDe = (nome) => {
    const k = nome || SEM_ETAPA;
    if (!etapas.has(k)) etapas.set(k, { etapa: k, rodadas: new Map() });
    return etapas.get(k);
  };
  const rodadaDe = (e, tipo, n) => {
    const k = `${tipo || 'principal'}#${n || 1}`;
    if (!e.rodadas.has(k)) {
      e.rodadas.set(k, { chave: k, tipoDeRodada: tipo || 'principal', rodada: n || 1,
        invocacoes: [], resultados: [] });
    }
    return e.rodadas.get(k);
  };
  invocacoes.forEach((i) => rodadaDe(etapaDe(i.etapa), i.tipo_de_rodada, i.rodada).invocacoes.push(i));
  resultados.forEach((r) => rodadaDe(etapaDe(r.etapa), r.tipo_de_rodada, r.rodada).resultados.push(r));

  return {
    total: totais(invocacoes),
    etapas: [...etapas.values()].map((e) => {
      const rodadas = [...e.rodadas.values()].map((r) => ({ ...r, total: totais(r.invocacoes) }));
      const todas = rodadas.flatMap((r) => r.invocacoes);
      return {
        etapa: e.etapa,
        rodadas,
        total: totais(todas),
        loops: rodadas.filter((r) => r.tipoDeRodada !== 'principal' && r.tipoDeRodada !== 'delegacao').length,
      };
    }),
  };
}

// ─── Agente ──────────────────────────────────────────────────────────────────

/** Rótulo que a API usa para invocação sem projeto (loops e quebra por projeto). */
export const SEM_PROJETO = '(sem projeto)';
